import React, { useCallback, useEffect, useState } from "react";

import Shell from "../layout/Shell";
import TeleprompterRecorder from "../components/TeleprompterRecorder";
import { api, formatApiError } from "../lib/api";
import { EmptyState, ErrorBanner, LoadingCard, Pill } from "../lib/ui";

// -----------------------------------------------------------------------
// Founder Videos (2026-08-28)
//
// 9-slot library for Roger's 45-second product explainers.  Each slot
// supports: draft script from Vault → owner edit → teleprompter record
// → object-storage upload → public playback.
// -----------------------------------------------------------------------

const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

function fmtDate(v) {
  if (!v) return "\u2014";
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toLocaleString(undefined, {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch (e) {
    return String(v);
  }
}

function statusOf(slot) {
  if (slot.published && slot.filename) return "published";
  if (slot.filename) return "recorded";
  if (slot.script && slot.script.trim().length > 0) return "drafted";
  return "missing";
}

function StatusPill({ status }) {
  if (status === "published") return <Pill tone="success">Published</Pill>;
  if (status === "recorded") return <Pill tone="warn">Recorded</Pill>;
  if (status === "drafted") return <Pill tone="muted">Drafted</Pill>;
  return <Pill tone="muted">Missing</Pill>;
}

export default function FounderVideos() {
  const [state, setState] = useState({
    slots: [],
    total: 0,
    published: 0,
  });
  const [selectedKey, setSelectedKey] = useState(null);
  const [scriptDraft, setScriptDraft] = useState("");
  const [savingScript, setSavingScript] = useState(false);
  const [generatingScript, setGeneratingScript] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRecorder, setShowRecorder] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/videos`);
      setState(r.data);
      if (!selectedKey && r.data.slots?.length) {
        setSelectedKey(r.data.slots[0].product_key);
        setScriptDraft(r.data.slots[0].script || "");
      }
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [selectedKey]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selected = state.slots.find((s) => s.product_key === selectedKey) || null;

  const pickSlot = (slot) => {
    setSelectedKey(slot.product_key);
    setScriptDraft(slot.script || "");
    setError(null);
  };

  const generateScript = async () => {
    if (!selected) return;
    setGeneratingScript(true);
    setError(null);
    try {
      // LLM calls can take 20-40s; override the default 15s axios timeout.
      const r = await api.post(
        `/videos/${selected.product_key}/generate-script`,
        {},
        { timeout: 60000 },
      );
      const draft = r.data?.draft || "";
      setScriptDraft(draft);
      // Refresh so the row gets a saved script if it was inserted.
      await load();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setGeneratingScript(false);
    }
  };

  const saveScript = async () => {
    if (!selected) return;
    if (!scriptDraft.trim()) {
      setError("Script cannot be empty.");
      return;
    }
    setSavingScript(true);
    setError(null);
    try {
      await api.put(`/videos/${selected.product_key}/script`, {
        script: scriptDraft,
      });
      await load();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSavingScript(false);
    }
  };

  const uploadVideo = async ({ blob, mime, durationSeconds }) => {
    if (!selected) return;
    const form = new FormData();
    // Give the file a safe name so the extension survives.
    const ext = mime && mime.includes("mp4") ? "mp4" : "webm";
    form.append("file", blob, `${selected.product_key}-take.${ext}`);
    if (durationSeconds) form.append("duration_seconds", String(durationSeconds));
    try {
      // NOTE: axios instance has JSON content-type; use fetch directly so
      // the browser sets the correct multipart boundary.
      const resp = await fetch(
        `${backendUrl}/api/brand-os/videos/${selected.product_key}/upload`,
        {
          method: "POST",
          body: form,
          credentials: "include",
        },
      );
      if (!resp.ok) {
        const text = await resp.text().catch(() => "");
        throw new Error(text || `Upload failed (${resp.status})`);
      }
      setShowRecorder(false);
      await load();
    } catch (e) {
      throw e;
    }
  };

  const deleteVideo = async () => {
    if (!selected) return;
    if (
      !window.confirm(
        `Delete the ${selected.product_display} video? This clears the slot.`,
      )
    ) {
      return;
    }
    try {
      await api.delete(`/videos/${selected.product_key}`);
      await load();
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  return (
    <Shell title="Founder Videos">
      <div className="bo-page" data-testid="founder-videos">
        <div style={{ marginBottom: 16 }}>
          <div className="bo-eyebrow">Marketing &middot; Video Pipeline</div>
          <h1
            style={{
              fontFamily: "var(--bo-serif)",
              fontSize: 28,
              margin: "4px 0 4px 0",
            }}
          >
            Founder Videos
          </h1>
          <p
            style={{
              color: "var(--bo-muted)",
              margin: 0,
              fontSize: 14,
              maxWidth: 720,
            }}
          >
            One 45-second explainer per product. Draft a script from your
            Vault, record it in the teleprompter, and it&rsquo;s ready to send
            to founding subscribers as their weekly note. Videos live in
            secure object storage &mdash; deployments won&rsquo;t wipe them.
          </p>
        </div>

        <div
          className="bo-card"
          style={{
            padding: 14,
            marginBottom: 12,
            display: "flex",
            gap: 24,
            flexWrap: "wrap",
          }}
          data-testid="videos-summary"
        >
          <SummaryStat label="Slots" value={state.total} />
          <SummaryStat label="Published" value={state.published} tone="success" />
          <SummaryStat
            label="Remaining"
            value={Math.max(0, (state.total || 0) - (state.published || 0))}
            tone="warn"
          />
        </div>

        {error && <ErrorBanner error={error} testId="videos-error" />}
        {loading && <LoadingCard />}
        {!loading && (!state.slots || state.slots.length === 0) && (
          <EmptyState
            title="No product slots"
            description="The 9-product flagship order should populate automatically. Try refreshing."
            testId="videos-empty"
          />
        )}

        {!loading && state.slots && state.slots.length > 0 && (
          <div
            style={{
              display: "grid",
              gap: 12,
              gridTemplateColumns: "minmax(280px, 1fr) 2fr",
            }}
          >
            {/* Slot list */}
            <div
              className="bo-card"
              style={{ padding: 0, overflow: "hidden" }}
              data-testid="videos-slot-list"
            >
              {state.slots.map((slot, idx) => {
                const st = statusOf(slot);
                const isSel = slot.product_key === selectedKey;
                return (
                  <button
                    key={slot.product_key}
                    onClick={() => pickSlot(slot)}
                    data-testid={`videos-slot-${slot.product_key}`}
                    style={{
                      display: "block",
                      width: "100%",
                      textAlign: "left",
                      padding: "12px 16px",
                      border: 0,
                      borderTop:
                        idx === 0
                          ? "none"
                          : "1px solid var(--bo-border, #e6e2d8)",
                      background: isSel
                        ? "var(--bo-accent-tint, #eef4ff)"
                        : "var(--bo-panel, #fff)",
                      cursor: "pointer",
                      fontFamily: "inherit",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: 3,
                      }}
                    >
                      <span
                        style={{
                          fontFamily: "var(--bo-serif)",
                          fontWeight: 600,
                          fontSize: 15,
                        }}
                      >
                        {idx + 1}. {slot.product_display}
                      </span>
                      <span style={{ marginLeft: "auto" }}>
                        <StatusPill status={st} />
                      </span>
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: "var(--bo-muted)",
                      }}
                    >
                      {slot.duration_seconds
                        ? `${Math.round(slot.duration_seconds)}s`
                        : slot.script
                        ? `${(slot.script || "").split(/\s+/).length} words`
                        : "no script yet"}
                      {slot.updated_at ? ` · ${fmtDate(slot.updated_at)}` : ""}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Detail */}
            {selected && (
              <div
                className="bo-card"
                style={{ padding: 20 }}
                data-testid="videos-detail"
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 6,
                  }}
                >
                  <StatusPill status={statusOf(selected)} />
                  <Pill tone="muted">{selected.product_key}</Pill>
                </div>
                <h2
                  style={{
                    fontFamily: "var(--bo-serif)",
                    fontSize: 22,
                    margin: "4px 0 4px 0",
                  }}
                  data-testid="videos-detail-title"
                >
                  {selected.product_display} &mdash; 45 seconds
                </h2>
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--bo-muted)",
                    marginBottom: 14,
                  }}
                >
                  {selected.script_source
                    ? `Source: ${selected.script_source}`
                    : "No script yet"}
                  {selected.updated_at
                    ? ` · updated ${fmtDate(selected.updated_at)}`
                    : ""}
                </div>

                {/* Playback */}
                {selected.public_url && (
                  <div style={{ marginBottom: 14 }} data-testid="videos-playback">
                    <video
                      controls
                      src={`${backendUrl}${selected.public_url}`}
                      style={{
                        width: "100%",
                        maxHeight: 320,
                        background: "#000",
                        borderRadius: 8,
                      }}
                    />
                  </div>
                )}

                {/* Script editor */}
                <label
                  className="bo-eyebrow"
                  style={{ display: "block", marginBottom: 6 }}
                >
                  Script (spoken text)
                </label>
                <textarea
                  className="bo-input"
                  value={scriptDraft}
                  onChange={(e) => setScriptDraft(e.target.value)}
                  data-testid="videos-script-editor"
                  placeholder="Draft your 45-second script here or click 'Generate from Vault'."
                  style={{
                    width: "100%",
                    minHeight: 160,
                    padding: 12,
                    fontFamily: "var(--bo-serif)",
                    fontSize: 15,
                    lineHeight: 1.5,
                  }}
                />

                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    marginTop: 12,
                    flexWrap: "wrap",
                  }}
                >
                  <button
                    className="bo-btn bo-btn--ghost"
                    onClick={generateScript}
                    disabled={generatingScript}
                    data-testid="videos-generate-script"
                    style={{ padding: "10px 16px" }}
                  >
                    {generatingScript ? "Drafting\u2026" : "Generate from Vault"}
                  </button>
                  <button
                    className="bo-btn bo-btn--primary"
                    onClick={saveScript}
                    disabled={savingScript || !scriptDraft.trim()}
                    data-testid="videos-save-script"
                    style={{ padding: "10px 16px" }}
                  >
                    {savingScript ? "Saving\u2026" : "Save script"}
                  </button>
                  <button
                    className="bo-btn bo-btn--primary"
                    onClick={() => setShowRecorder(true)}
                    disabled={!scriptDraft.trim()}
                    data-testid="videos-open-recorder"
                    style={{ padding: "10px 16px" }}
                  >
                    {selected.filename ? "Re-record" : "Record teleprompter"}
                  </button>
                  {selected.filename && (
                    <button
                      className="bo-btn bo-btn--ghost"
                      onClick={deleteVideo}
                      data-testid="videos-delete"
                      style={{
                        padding: "10px 16px",
                        color: "#8a2b0a",
                        borderColor: "#e6c9c0",
                      }}
                    >
                      Delete video
                    </button>
                  )}
                </div>

                <div
                  style={{
                    marginTop: 16,
                    padding: 12,
                    background: "var(--bo-panel-alt, #f6f4ef)",
                    borderRadius: 6,
                    fontSize: 12,
                    color: "var(--bo-muted)",
                    lineHeight: 1.5,
                  }}
                >
                  <strong>Tip:</strong> the LLM drafts from your approved Vault
                  content. Edit it in Roger&rsquo;s voice, then hit record. Videos
                  upload to secure object storage &mdash; nothing gets stored on
                  the app pod.
                </div>
              </div>
            )}
          </div>
        )}

        {showRecorder && selected && (
          <TeleprompterRecorder
            productKey={selected.product_key}
            productDisplay={selected.product_display}
            script={scriptDraft}
            onClose={() => setShowRecorder(false)}
            onUpload={uploadVideo}
          />
        )}
      </div>
    </Shell>
  );
}

function SummaryStat({ label, value, tone }) {
  const color =
    tone === "success"
      ? "#1f6c3f"
      : tone === "warn"
      ? "#8a5a0a"
      : tone === "muted"
      ? "var(--bo-muted)"
      : "var(--bo-ink)";
  return (
    <div>
      <div
        className="bo-eyebrow"
        style={{ fontSize: 11, color: "var(--bo-muted)" }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: "var(--bo-serif)",
          fontSize: 24,
          color,
          fontWeight: 600,
        }}
        data-testid={`videos-summary-${label.toLowerCase()}`}
      >
        {value ?? 0}
      </div>
    </div>
  );
}
